import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from 'src/redux/rootReducerType';
import type { UserProfileData } from 'src/services/PersonalInfo.service';

export interface NewRenterInterfaceUser {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  displayName: string;
  primary_mobile_phone?: string;
  communication_preference?: string;
  sms_opt_in_otp_2fa?: boolean;
  sms_opt_in_customer_care?: boolean;
  sms_opt_in_account_notifications?: boolean;
  receive_renter_notifications?: boolean;
  receive_renter_notifications_email?: boolean;
  email_verified?: boolean;
  phone_verified?: boolean;
  impersonator?: { session_id: string } | null;
}

// ---------------------------------------------------------------------------
// Account Hub types
// ---------------------------------------------------------------------------

export interface ElevatedAuthState {
  verified: boolean;
  verified_at: string | null;
  expires_at: string | null;
  totp_enabled: boolean;
  method: 'otp' | 'totp' | null;
  loading: boolean;
  error: string | null;
  /**
   * True once the authenticator status has been fetched at least once. Lets
   * consumers (e.g. household contact editing) know `totp_enabled` is
   * authoritative before rendering, avoiding a disabled/enabled flip.
   * Optional so partial test fixtures don't have to mock the whole shape.
   */
  statusChecked?: boolean;
}

export interface ActiveSession {
  jti: string;
  is_current: boolean;
  user_agent: string;
  device_label: string;
  ip_address: string | null;
  location_city: string;
  location_country: string;
  created_at: string | null;
  last_used_at: string | null;
  expires_at: string | null;
}

export interface LoginSecurityState {
  totp_enabled: boolean;
  totp_confirmed_at: string | null;
  backup_codes_remaining: number;
  backup_codes_generated_at: string | null;
  last_hydrated_at: string | null;
  loading: boolean;
  error: string | null;
}

export interface AccountHubState {
  selectedUserId: number | null;
  selectedHomePageMemberId: number | null;
  elevatedAuth: ElevatedAuthState;
  sessions: {
    items: ActiveSession[];
    last_hydrated_at: string | null;
    loading: boolean;
    error: string | null;
  };
  loginSecurity: LoginSecurityState;
  personalInfo: {
    dataByUser: Record<number, UserProfileData | null>;
    loadingByUser: Record<number, boolean>;
    errorByUser: Record<number, string | null>;
    revealedSsnByUser: Record<number, string | null>;
  };
}

const initialAccountHub: AccountHubState = {
  selectedUserId: null,
  selectedHomePageMemberId: null,
  elevatedAuth: {
    verified: false,
    verified_at: null,
    expires_at: null,
    totp_enabled: false,
    method: null,
    loading: false,
    error: null,
    statusChecked: false,
  },
  sessions: {
    items: [],
    last_hydrated_at: null,
    loading: false,
    error: null,
  },
  loginSecurity: {
    totp_enabled: false,
    totp_confirmed_at: null,
    backup_codes_remaining: 0,
    backup_codes_generated_at: null,
    last_hydrated_at: null,
    loading: false,
    error: null,
  },
  personalInfo: {
    dataByUser: {},
    loadingByUser: {},
    errorByUser: {},
    revealedSsnByUser: {},
  },
};

// ---------------------------------------------------------------------------
// Slice state
// ---------------------------------------------------------------------------

interface NewRenterInterfaceUserState {
  user: NewRenterInterfaceUser | null;
  savedSignatureXfdf: string | null;
  savedInitialsText: string | null;
  savedInitialsFont: string | null;
  savedInitialsXfdf: string | null;
  accountHub: AccountHubState;
  hasPendingVerifications: boolean;
}

const initialState: NewRenterInterfaceUserState = {
  user: null,
  savedSignatureXfdf: null,
  savedInitialsText: null,
  savedInitialsFont: null,
  savedInitialsXfdf: null,
  accountHub: initialAccountHub,
  hasPendingVerifications: false,
};

// ---------------------------------------------------------------------------
// Slice
// ---------------------------------------------------------------------------

const NewRenterInterfaceUserSlice = createSlice({
  name: 'newRenterInterfaceUser',
  initialState,
  reducers: {
    setUser(state, action: PayloadAction<NewRenterInterfaceUser>) {
      state.user = action.payload;
    },
    setHasPendingVerifications(state, action: PayloadAction<boolean>) {
      state.hasPendingVerifications = action.payload;
    },
    updateUser(state, action: PayloadAction<Record<string, unknown>>) {
      if (state.user) {
        const allowed: (keyof NewRenterInterfaceUser)[] = [
          'id',
          'email',
          'first_name',
          'last_name',
          'displayName',
          'primary_mobile_phone',
          'communication_preference',
          'sms_opt_in_otp_2fa',
          'sms_opt_in_customer_care',
          'sms_opt_in_account_notifications',
          'receive_renter_notifications',
          'receive_renter_notifications_email',
          'email_verified',
          'phone_verified',
        ];
        const picked: Partial<NewRenterInterfaceUser> = {};
        for (const key of allowed) {
          if (key in action.payload) {
            (picked as Record<string, unknown>)[key] = action.payload[key];
          }
        }
        Object.assign(state.user, picked);
      }
    },
    clearUser(state) {
      // Full slice reset on logout. Previously only nulled `user`, which
      // left selectedHomePageMemberId, accountHub.elevatedAuth,
      // accountHub.personalInfo (incl. revealedSsnByUser — PII!), saved
      // signatures/initials, and other per-user state behind. With
      // whitelist=[] in rootPersistConfig this didn't survive a page
      // refresh, but a logout-then-login cycle in the same tab would leak
      // the previous user's session data into the new user's view.
      state.user = null;
      state.savedSignatureXfdf = null;
      state.savedInitialsText = null;
      state.savedInitialsFont = null;
      state.savedInitialsXfdf = null;
      state.accountHub = initialAccountHub;
      state.hasPendingVerifications = false;
    },
    setSavedSignature(state, action: PayloadAction<string | null>) {
      state.savedSignatureXfdf = action.payload;
    },
    setSavedInitials(
      state,
      action: PayloadAction<
        | { text: string | null; font: string | null; xfdf?: never }
        | { xfdf: string | null; text?: never; font?: never }
      >
    ) {
      if ('xfdf' in action.payload) {
        state.savedInitialsXfdf = action.payload.xfdf;
        state.savedInitialsText = null;
        state.savedInitialsFont = null;
      } else {
        state.savedInitialsText = action.payload.text;
        state.savedInitialsFont = action.payload.font;
        state.savedInitialsXfdf = null;
      }
    },

    // --- Account Hub: Selected User ---
    setAccountHubUser(state, action: PayloadAction<number | null>) {
      state.accountHub.selectedUserId = action.payload;
    },

    // --- Account Hub: Home Page Member Selector ---
    setSelectedHomePageMemberId(
      state,
      action: PayloadAction<number | null>
    ): void {
      state.accountHub.selectedHomePageMemberId = action.payload;
    },

    setElevatedAuthTotpEnabled(state, action: PayloadAction<boolean>) {
      state.accountHub.elevatedAuth.totp_enabled = action.payload;
      state.accountHub.elevatedAuth.statusChecked = true;
    },
    // --- Account Hub: Elevated Auth ---
    setElevatedAuth(
      state,
      action: PayloadAction<{
        verified: boolean;
        verified_at?: string;
        expires_at?: string;
        totp_enabled?: boolean;
        method?: 'otp' | 'totp' | null;
      }>
    ) {
      state.accountHub.elevatedAuth.verified = action.payload.verified;
      state.accountHub.elevatedAuth.verified_at =
        action.payload.verified_at ?? null;
      state.accountHub.elevatedAuth.expires_at =
        action.payload.expires_at ?? null;
      if (action.payload.totp_enabled !== undefined) {
        state.accountHub.elevatedAuth.totp_enabled =
          action.payload.totp_enabled;
      }
      if (action.payload.method !== undefined) {
        state.accountHub.elevatedAuth.method = action.payload.method ?? null;
      }
      state.accountHub.elevatedAuth.error = null;
      state.accountHub.elevatedAuth.statusChecked = true;
    },
    clearElevatedAuth(state) {
      // Preserve totp_enabled across session expiries. TOTP status is a
      // property of the user, not of the elevated session — wiping it here
      // would race with ElevatedAuthGate's async fetchStatus() and cause
      // VerifyCodeModal to mount with a stale totp_enabled=false, sending
      // an OTP to a user who should have been prompted for TOTP. The
      // elevation fields (verified, verified_at, expires_at, method) are
      // the ones that should actually reset.
      const { totp_enabled, statusChecked } = state.accountHub.elevatedAuth;
      state.accountHub.elevatedAuth = {
        ...initialAccountHub.elevatedAuth,
        totp_enabled,
        statusChecked,
      };
    },
    setElevatedAuthLoading(state, action: PayloadAction<boolean>) {
      state.accountHub.elevatedAuth.loading = action.payload;
    },
    setElevatedAuthError(state, action: PayloadAction<string | null>) {
      state.accountHub.elevatedAuth.error = action.payload;
      state.accountHub.elevatedAuth.loading = false;
    },

    // --- Account Hub: Sessions ---
    setSessions(state, action: PayloadAction<ActiveSession[]>) {
      state.accountHub.sessions.items = action.payload;
      state.accountHub.sessions.last_hydrated_at = new Date().toISOString();
      state.accountHub.sessions.loading = false;
      state.accountHub.sessions.error = null;
    },
    removeSession(state, action: PayloadAction<string>) {
      state.accountHub.sessions.items = state.accountHub.sessions.items.filter(
        (s) => s.jti !== action.payload
      );
    },
    setSessionsLoading(state, action: PayloadAction<boolean>) {
      state.accountHub.sessions.loading = action.payload;
    },
    setSessionsError(state, action: PayloadAction<string | null>) {
      state.accountHub.sessions.error = action.payload;
      state.accountHub.sessions.loading = false;
    },

    // --- Account Hub: Login Security ---
    setLoginSecurity(
      state,
      action: PayloadAction<Partial<LoginSecurityState>>
    ) {
      Object.assign(state.accountHub.loginSecurity, action.payload);
      state.accountHub.loginSecurity.last_hydrated_at =
        new Date().toISOString();
      state.accountHub.loginSecurity.loading = false;
      state.accountHub.loginSecurity.error = null;
    },
    setLoginSecurityLoading(state, action: PayloadAction<boolean>) {
      state.accountHub.loginSecurity.loading = action.payload;
    },
    setLoginSecurityError(state, action: PayloadAction<string | null>) {
      state.accountHub.loginSecurity.error = action.payload;
      state.accountHub.loginSecurity.loading = false;
    },

    // --- Account Hub: Personal Info ---
    setPersonalInfo(
      state,
      action: PayloadAction<{ userId: number; data: UserProfileData }>
    ) {
      state.accountHub.personalInfo.dataByUser[action.payload.userId] =
        action.payload.data;
      state.accountHub.personalInfo.loadingByUser[action.payload.userId] =
        false;
      state.accountHub.personalInfo.errorByUser[action.payload.userId] = null;
    },
    setPersonalInfoLoading(
      state,
      action: PayloadAction<{ userId: number; loading: boolean }>
    ) {
      state.accountHub.personalInfo.loadingByUser[action.payload.userId] =
        action.payload.loading;
    },
    setPersonalInfoError(
      state,
      action: PayloadAction<{ userId: number; error: string | null }>
    ) {
      state.accountHub.personalInfo.errorByUser[action.payload.userId] =
        action.payload.error;
      state.accountHub.personalInfo.loadingByUser[action.payload.userId] =
        false;
    },
    setRevealedSsn(
      state,
      action: PayloadAction<{ userId: number; ssn: string }>
    ) {
      state.accountHub.personalInfo.revealedSsnByUser[action.payload.userId] =
        action.payload.ssn;
    },
    clearRevealedSsn(state, action: PayloadAction<number>) {
      state.accountHub.personalInfo.revealedSsnByUser[action.payload] = null;
    },
  },
});

export const {
  setUser,
  updateUser,
  clearUser,
  setSavedSignature,
  setSavedInitials,
  setHasPendingVerifications,
  // Account Hub
  setAccountHubUser,
  setSelectedHomePageMemberId,
  setElevatedAuth,
  setElevatedAuthTotpEnabled,
  clearElevatedAuth,
  setElevatedAuthLoading,
  setElevatedAuthError,
  setSessions,
  removeSession,
  setSessionsLoading,
  setSessionsError,
  setLoginSecurity,
  setLoginSecurityLoading,
  setLoginSecurityError,
  // Personal Info
  setPersonalInfo,
  setPersonalInfoLoading,
  setPersonalInfoError,
  setRevealedSsn,
  clearRevealedSsn,
} = NewRenterInterfaceUserSlice.actions;

export default NewRenterInterfaceUserSlice.reducer;

// ---------------------------------------------------------------------------
// Selectors
// ---------------------------------------------------------------------------

export const selectElevatedAuth = (s: RootState): ElevatedAuthState =>
  s.newRenterInterfaceUser.accountHub.elevatedAuth;

export const selectSessions = (s: RootState): AccountHubState['sessions'] =>
  s.newRenterInterfaceUser.accountHub.sessions;

export const selectLoginSecurity = (s: RootState): LoginSecurityState =>
  s.newRenterInterfaceUser.accountHub.loginSecurity;

export const selectElevatedAuthIsValid = (s: RootState): boolean => {
  const { verified, expires_at } =
    s.newRenterInterfaceUser.accountHub.elevatedAuth;
  if (!verified || !expires_at) {
    return false;
  }
  return new Date(expires_at).getTime() > Date.now();
};

/**
 * True when the session has a live elevated-auth window established via TOTP
 * (as opposed to an email/SMS OTP). Managing another member's contacts requires
 * TOTP, so this is the single predicate shared by the household edit screen and
 * the MemberContactEditor gate.
 */
export const selectIsTotpElevated = (s: RootState): boolean => {
  const { verified, expires_at, method } =
    s.newRenterInterfaceUser.accountHub.elevatedAuth;
  if (!verified || !expires_at || method !== 'totp') {
    return false;
  }
  return new Date(expires_at).getTime() > Date.now();
};

export const selectAccountHubUserId = (s: RootState): number | null =>
  s.newRenterInterfaceUser.accountHub.selectedUserId;

export const selectPersonalInfoFor =
  (userId: number) =>
  (s: RootState): UserProfileData | null =>
    s.newRenterInterfaceUser.accountHub.personalInfo.dataByUser[userId] ?? null;

export const selectPersonalInfoLoadingFor =
  (userId: number) =>
  (s: RootState): boolean =>
    s.newRenterInterfaceUser.accountHub.personalInfo.loadingByUser[userId] ??
    false;

export const selectPersonalInfoErrorFor =
  (userId: number) =>
  (s: RootState): string | null =>
    s.newRenterInterfaceUser.accountHub.personalInfo.errorByUser[userId] ??
    null;

export const selectRevealedSsnFor =
  (userId: number) =>
  (s: RootState): string | null =>
    s.newRenterInterfaceUser.accountHub.personalInfo.revealedSsnByUser[
      userId
    ] ?? null;

export const selectHasPendingVerifications = (s: RootState): boolean =>
  s.newRenterInterfaceUser.hasPendingVerifications;

export const selectNewRenterInterfaceUser = (
  s: RootState
): NewRenterInterfaceUser | null => s.newRenterInterfaceUser.user;
